import { Box, Typography } from "@mui/material";
import DesignerAuthenticationControls from "../../components/DesignerAuthenticationControls";
import ComponentFactory from "../Components/ComponentFactory";
import useViewExpressionEvaluatorContext from "../Components/View/useViewExpressionEvaluatorContext";
import { useDesignerMode } from "../Contexts/DesignerModeContext";
import { SHARED_COMPONENT_ROOT_TYPE } from "../Types/DesignerMessages";

/**
 * Designer Preview Route
 * Embedded in iframe by app-portal for live preview
 * Consumes DesignerModeContext which manages postMessage communication at the root level
 */
export function DesignerPreviewRoute() {
  const designerMode = useDesignerMode();
  const model = designerMode?.model ?? null;

  // Unwrap SharedComponentRoot if that's what we received
  const componentToRender = model?.Type === SHARED_COMPONENT_ROOT_TYPE && (model as any).Children?.[0] ? (model as any).Children[0] : model;

  // For SharedComponents, we unwrapped from the container, so the component is at path [child.Id]
  // For Views, the view itself is at path [], and its children will be at [child.Id], etc.
  const componentPath = model?.Type === SHARED_COMPONENT_ROOT_TYPE && (model as any).Children?.[0] ? [(model as any).Children[0].Id] : [];

  // Create expression evaluator context (mock for edit mode)
  const viewContext = useViewExpressionEvaluatorContext(componentToRender, {});

  if (!componentToRender) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <DesignerAuthenticationControls />
        <Typography variant="h6" color="text.secondary">
          Waiting for component model...
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          This preview is embedded in the designer. Send a component model via postMessage to render.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2, backgroundColor: "grey.50", height: "100vh", overflow: "auto" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        🎨 Live Preview Mode (Read-Only)
      </Typography>

      <DesignerAuthenticationControls />
      {/* Render the component using ComponentFactory */}
      <ComponentFactory key={componentToRender.Id} component={componentToRender} parent={null} context={viewContext} componentPath={componentPath} />
    </Box>
  );
}
